import {AttendeeAnalytics} from "../../domain/attendee-analytics.entity";
import type {PaginationInfo} from "../../domain/pagination.types";
import type {V2Attendee, V2AttendeesData} from "./sales-insight.mapper.types";

/**
 * V2 mapper for AttendeeAnalytics.
 * Trivial since domain shape mirrors v2 - mainly parses createdAt to Date.
 *
 * Pure TypeScript - no React imports.
 */
export const AttendeeAnalyticsMapperV2 = {
  /**
   * Maps a single v2 attendee to domain entity.
   */
  toDomain(data: V2Attendee): AttendeeAnalytics {
    return AttendeeAnalytics.create({
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      emailAddress: data.emailAddress ?? "",
      amount: data.amount ?? 0,
      currency: data.currency ?? "",
      reference: data.reference ?? "",
      createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
    });
  },

  /**
   * Maps v2 attendees list to domain entities.
   */
  toDomainList(attendees: V2Attendee[] | undefined): AttendeeAnalytics[] {
    if (!attendees || !Array.isArray(attendees)) {
      return [];
    }
    return attendees.map((attendee) => this.toDomain(attendee));
  },

  /**
   * Maps v2 pagination to domain PaginationInfo.
   */
  toPaginationInfo(
    data: V2AttendeesData | undefined,
    page: number,
    perPage: number
  ): PaginationInfo {
    return {
      page,
      perPage,
      hasNext: data?.hasNextPage ?? false,
      hasPrev: data?.hasPrevPage ?? false,
      total: data?.total ?? 0,
    };
  },

  /**
   * Maps full v2 attendees response to domain structure.
   */
  toAttendeesResult(
    data: V2AttendeesData | undefined,
    page: number,
    perPage: number
  ): {attendees: AttendeeAnalytics[]; pagination: PaginationInfo} {
    const attendees = this.toDomainList(data?.data);
    const pagination = this.toPaginationInfo(data, page, perPage);
    return {attendees, pagination};
  },
};
